import { QueryClientProvider } from '@tanstack/react-query'
import { Suspense, lazy, useState, type ReactNode } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { AppShell } from './components/AppShell.js'
import { ReleaseProvider } from './components/UpdateNotice.js'
import { OpenJobFromPath } from './components/JobOverlay.js'
import { FieldInbox } from './components/field/FieldInbox.js'
import { FieldJobs } from './components/field/FieldJobs.js'
import { FieldMe } from './components/field/FieldMe.js'
import { FieldTeam } from './components/field/FieldTeam.js'
import { Spinner } from './components/ui.js'
import { useIsField } from './lib/media.js'
import { RealtimeProvider } from './lib/realtime.js'
import { SessionProvider, useSession } from './lib/session.js'
import { TRPCProvider, makeQueryClient, trpcClient } from './lib/trpc.js'
import { AcceptInvite } from './routes/AcceptInvite.js'
import { AgreementDetail } from './routes/AgreementDetail.js'
import { Agreements } from './routes/Agreements.js'
import { ClientDetail } from './routes/ClientDetail.js'
import { Clients } from './routes/Clients.js'
import { ForgotPassword } from './routes/ForgotPassword.js'
import { Inbox } from './routes/Inbox.js'
import { Invoices } from './routes/Invoices.js'
import { Jobs } from './routes/Jobs.js'
import { Login } from './routes/Login.js'
import { Materials } from './routes/Materials.js'
import { InvoiceView } from './routes/public/InvoiceView.js'
import { ProposalView } from './routes/public/ProposalView.js'
import { PersonDetail } from './routes/PersonDetail.js'
import { Quotes } from './routes/Quotes.js'
import { ResetPassword } from './routes/ResetPassword.js'
import { Settings } from './routes/Settings.js'
import { Tasks } from './routes/Tasks.js'
import { Today } from './routes/Today.js'

// The scheduling module pulls in dnd-kit and MapLibre — the map renderer alone
// is larger than the rest of the app — and it is a desktop screen most
// technicians never open. Splitting it keeps that weight off the first load on
// a phone.
const Schedule = lazy(() =>
  import('./routes/schedule/Schedule.js').then((module) => ({ default: module.Schedule })),
)
const RoutePlanner = lazy(() =>
  import('./routes/schedule/planner/RoutePlanner.js').then((module) => ({
    default: module.RoutePlanner,
  })),
)

export function App() {
  const [queryClient] = useState(makeQueryClient)

  return (
    <QueryClientProvider client={queryClient}>
      <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
        <BrowserRouter>
          <SessionProvider>
            <ReleaseProvider>
              <Shell />
            </ReleaseProvider>
          </SessionProvider>
        </BrowserRouter>
      </TRPCProvider>
    </QueryClientProvider>
  )
}

function Shell() {
  const { user, isLoading } = useSession()

  return (
    <RealtimeProvider enabled={Boolean(user)}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        {/* A customer's proposal link. Outside the signed-in shell on
            purpose: the token is the credential. Decision 80. */}
        <Route path="/p/:token" element={<ProposalView />} />
        {/* The same for an invoice's link. Decision 88. */}
        <Route path="/i/:token" element={<InvoiceView />} />

        <Route
          element={
            isLoading ? <FullPageSpinner /> : user ? <AppShell /> : <RedirectToLogin />
          }
        >
          <Route index element={<Today />} />
          <Route path="jobs" element={<ByDevice field={<FieldJobs />} desk={<Jobs />} />} />
          <Route path="worklist" element={<Jobs />} />
          <Route path="team" element={<ByDevice field={<FieldTeam />} desk={<Navigate to="/board" replace />} />} />
          <Route
            path="me"
            element={<ByDevice field={<FieldMe />} desk={<Navigate to="/settings?tab=account" replace />} />}
          />

          {/* A job has no page. It opens as a popup over whatever screen you
              are on, from `?jobId=`, drawn by `JobOverlay` in the shell. These
              two are the addresses that used to be pages, kept working for
              links already sent. Decision 61. */}
          <Route path="jobs/:jobId" element={<OpenJobFromPath />} />
          <Route path="jobs/:jobId/report/:jobServiceId" element={<OpenJobFromPath />} />

          <Route
            path="board"
            element={
              <Suspense fallback={<FullPageSpinner />}>
                <Schedule />
              </Suspense>
            }
          />
          <Route
            path="board/plan"
            element={
              <Suspense fallback={<FullPageSpinner />}>
                <RoutePlanner />
              </Suspense>
            }
          />
          <Route path="inbox" element={<ByDevice field={<FieldInbox />} desk={<Inbox />} />} />
          <Route path="tasks" element={<Tasks />} />
          <Route path="clients" element={<Clients />} />
          <Route path="clients/:id" element={<ClientDetail />} />
          <Route path="agreements" element={<Agreements />} />
          <Route path="agreements/:id" element={<AgreementDetail />} />
          <Route path="quotes" element={<Quotes />} />
          <Route path="invoices" element={<Invoices />} />
          <Route path="materials" element={<Materials />} />
          <Route path="users" element={<Navigate to="/settings?tab=people" replace />} />

          {/* A person, unlike a job, does have a page. Rule 13 is about jobs:
              a job is glanced at over the board, and a person is a record you
              sit with — long enough to photograph a licence card into it. */}
          <Route path="people/:userId" element={<PersonDetail />} />

          <Route path="settings" element={<Settings />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </RealtimeProvider>
  )
}

/**
 * A route the field app draws differently. The same test as the shell's, so a
 * screen and its frame always agree. Decision 93.
 */
function ByDevice({ field, desk }: { field: ReactNode; desk: ReactNode }) {
  return <>{useIsField() ? field : desk}</>
}

function FullPageSpinner() {
  return (
    <div className="grid h-full place-items-center">
      <Spinner className="size-6 text-ink-400" />
    </div>
  )
}

/**
 * Keeps the destination so signing in lands where the person was headed.
 *
 * This is what makes a shared link work for someone who is not signed in:
 * they get the login page, then the page they were sent, rather than the
 * dashboard and a second trip through their messages to find the link again.
 */
function RedirectToLogin() {
  const location = useLocation()
  const next = `${location.pathname}${location.search}`

  return (
    <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />
  )
}
